import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  AccordionItemType,
  AccordionAction,
  AccordionState,
  accordionReducer,
  initialState,
} from './accordionReducer'

export const AccordionContext = createContext({
  addItem: ({ value = '', isOpen = false }: AccordionItemType) => {
    return
  },
  toggleItem: ({ value = '' }: AccordionItemType) => {
    return
  },
  toggleSingleItem: ({ value = '' }: AccordionItemType) => {
    return
  },
  addChild: ({ value = '', childValue = '' }: AccordionAction['payload']) => {
    return
  },
  type: '',
  state: {
    items: [] as AccordionItemType[],
  },
  collapsible: false,
  startOpen: false,
  variant: '',
  defaultValue: '',
})

type AccordionProviderValue = {
  type?: string
  collapsible?: boolean
  variant?: string
  defaultValue?: string
  value?: string
  startOpen?: boolean
  onValueChange?: (openedItems: string[] | string) => void
}

type AccordionProviderProps = {
  children: React.ReactNode
  value: AccordionProviderValue
}

export const AccordionProvider = ({
  children,
  value: {
    collapsible = false,
    type = '',
    variant = '',
    defaultValue = '',
    onValueChange,
    startOpen = false,
    value: controlledValue,
  } = {},
}: AccordionProviderProps) => {
  const [state, dispatch] = useReducer<React.Reducer<AccordionState, AccordionAction>>(
    accordionReducer,
    initialState,
  )
  const [currentOpenedItems, setCurrentOpenedItems] = useState<string[]>([])
  const previousValue = React.useRef<string>('')

  const [isToggling, setIsToggling] = useState(false)

  const timingOfadd = React.useRef<any>(null)
  const itemSize = React.useRef(0)
  const lastDefaultToOpen = React.useRef<null | string>(null)

  useEffect(() => {
    if (controlledValue && controlledValue !== previousValue.current && type === 'single') {
      toggleSingleItem({
        value: controlledValue,
      })

      previousValue.current = controlledValue
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue])

  useEffect(() => {
    itemSize.current = state.items.length
  }, [state.items])

  useEffect(() => {
    if (defaultValue && defaultValue !== lastDefaultToOpen.current) {
      clearTimeout(timingOfadd.current)

      timingOfadd.current = setTimeout(() => {
        const currentItem = state.items.find((item) => item.value === defaultValue)

        if (currentItem && !currentItem.isOpen) {
          const { value, level = 0 } = currentItem

          if (type === 'multiple' || level > 1) {
            openSelected({ value })
          }

          /* istanbul ignore else */
          if (type === 'single' && level === 1) {
            toggleSingleItem({ value })
          }

          lastDefaultToOpen.current = defaultValue
        }
      }, 100)
    }
  }, [defaultValue, state.items])

  useEffect(() => {
    if (isToggling) {
      if (typeof onValueChange === 'function') {
        const openedItems = state.items
          .filter((item) => item.isOpen)
          .map((item) => item.value) as string[]

        const isDifferent = JSON.stringify(openedItems) !== JSON.stringify(currentOpenedItems)

        /* istanbul ignore else */
        if (isDifferent) {
          onValueChange(type === 'single' ? openedItems[0] : openedItems)
          setCurrentOpenedItems(openedItems)
        }
      }
    }

    setIsToggling(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.items, isToggling])

  const addItem = useCallback(
    ({
      value = '',
      isOpen = false,
      triggerId = '',
      labelledId = '',
      level = 1,
      ref = null,
      childrenValue = [],
      parentValue = '',
      disabled = false,
    }: AccordionAction['payload']) => {
      dispatch({
        type: 'ADD_ITEM',
        payload: {
          value,
          isOpen: startOpen ? true : isOpen,
          triggerId,
          labelledId,
          ref,
          level,
          childrenValue,
          parentValue,
          disabled,
        },
      })
    },
    [],
  )

  const toggleItem = ({ value = '', level = 1 }: AccordionItemType) => {
    dispatch({
      type: 'TOGGLE_ITEM',
      payload: {
        value,
        level,
      },
    })
    setIsToggling(true)
  }

  const toggleSingleItem = ({ value = '', level = 1 }: AccordionItemType) => {
    dispatch({
      type: 'TOGGLE_SINGLE_ITEM',
      payload: {
        value,
        collapsible,
        level,
      },
    })
    setIsToggling(true)
  }

  const openSelected = ({ value = '' }: AccordionItemType) => {
    dispatch({
      type: 'OPEN_SELECTED',
      payload: {
        value,
      },
    })
  }

  const addChild = useCallback(({ value = '', childValue = '' }: AccordionAction['payload']) => {
    dispatch({
      type: 'ADD_CHILDREN',
      payload: {
        value,
        childValue,
      },
    })
  }, [])

  return (
    <AccordionContext.Provider
      value={{
        addItem,
        toggleItem,
        toggleSingleItem,
        addChild,
        state,
        type,
        collapsible,
        variant,
        defaultValue,
        startOpen,
      }}
    >
      {children}
    </AccordionContext.Provider>
  )
}

export const useAccordion = () => useContext(AccordionContext)

export default {
  ...AccordionContext,
  Provider: AccordionProvider,
}
